import React, { useEffect, useState } from 'react';
import { PLANS, fmtDate } from './data.jsx';
import { Icon } from './icons.jsx';
import { normStatus } from './lojas.jsx';
import { saIsRealTenantsMode } from './mvp-config.jsx';
import { formatPlanUpdateError, planService, resolvePlan, tenantService } from './services.jsx';
// Drawer de alteração de plano — Super Admin Tezus
// Planos selecionáveis: basic e pro (alinhados ao backend real).
// Enterprise: em breve/rascunho — NÃO pode ser aplicado a tenant.
// Motivo obrigatório + checkbox de confirmação de impacto.
const ChangePlanDrawer = ({ open, store, onClose, onConfirm }) => {
  const useReal = saIsRealTenantsMode();
  const [selectablePlans, setSelectablePlans] = useState(() =>
    (PLANS || []).filter(p => p.id === 'basic' || p.id === 'pro')
  );
  const [plansLoading,    setPlansLoading]    = useState(false);

  const [selectedPlanId,  setSelectedPlanId]  = useState(() => store?.plan?.id || 'basic');
  const [billingPeriod,   setBillingPeriod]   = useState('monthly');
  const [applyWhen,       setApplyWhen]       = useState('next_cycle');
  const [reason,          setReason]          = useState('');
  const [confirmed,       setConfirmed]       = useState(false);
  const [saving,          setSaving]          = useState(false);
  const [saveError,       setSaveError]       = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setPlansLoading(true);
    planService.listPlans()
      .then(() => {
        if (cancelled) return;
        const plans = (PLANS || []).filter(p => p.id === 'basic' || p.id === 'pro');
        if (plans.length) setSelectablePlans(plans);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setPlansLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (open && store) {
      setSelectedPlanId(store.plan?.id || 'basic');
      setBillingPeriod('monthly');
      const ns = typeof normStatus !== 'undefined' ? normStatus(store.status) : store.status;
      setApplyWhen(ns === 'trial' ? 'immediately' : 'next_cycle');
      setReason('');
      setConfirmed(false);
      setSaving(false);
      setSaveError(null);
    }
  }, [open, store]);

  if (!open || !store) return null;

  const selectedPlan   = selectablePlans.find(p => p.id === selectedPlanId) || selectablePlans[0] || store.plan;
  const currentPlan    = store.plan;
  const isSamePlan     = selectedPlanId === currentPlan?.id;
  const isUpgrade      = selectedPlan && currentPlan && selectedPlan.price > currentPlan.price;
  const isDowngrade    = selectedPlan && currentPlan && selectedPlan.price < currentPlan.price;
  const yearlyDiscount = 0.2;

  const displayPrice = billingPeriod === 'yearly'
    ? selectedPlan.price * 12 * (1 - yearlyDiscount)
    : selectedPlan.price;
  const priceLabel = billingPeriod === 'yearly' ? '/ano' : '/mês';
  const priceDelta = billingPeriod === 'monthly'
    ? (selectedPlan.price || 0) - (currentPlan.price || 0)
    : ((selectedPlan.price || 0) * 12 * (1 - yearlyDiscount)) - ((currentPlan.price || 0) * 12);

  const overLimits = [];
  if (isDowngrade && store.usage) {
    if (store.usage.pageviews > selectedPlan.pageviews)
      overLimits.push(`pageviews (${store.usage.pageviews.toLocaleString('pt-BR')} usados, limite ${selectedPlan.pageviews.toLocaleString('pt-BR')})`);
    if (store.usage.users > selectedPlan.users)
      overLimits.push(`usuários (${store.usage.users} ativos, limite ${selectedPlan.users})`);
  }

  const proratedToday = applyWhen === 'immediately' && !isSamePlan && store.status !== 'trial'
    ? Math.abs((selectedPlan.price || 0) - (currentPlan.price || 0)) * 0.6
    : 0;

  const fmtBRLDrawer = (v) => 'R$ ' + Math.round(v).toLocaleString('pt-BR');
  const canConfirm = !isSamePlan && reason.trim().length >= 5 && confirmed && !saving && !plansLoading;

  const handleConfirm = async () => {
    if (!canConfirm) return;
    const tenantId = store.tenant_id || String(store.id || '').replace(/^tenant_/, '');
    if (!tenantId) {
      setSaveError('ID do tenant não encontrado.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const apiResult = await tenantService.updatePlan(tenantId, {
        plan: selectedPlanId,
        reason: reason.trim(),
      });
      const updatedPlan = resolvePlan(apiResult.plan || selectedPlanId);
      onConfirm({ plan: updatedPlan, billingPeriod, apply: applyWhen, reason: reason.trim(), apiResult });
    } catch (e) {
      setSaveError(formatPlanUpdateError(e));
      setSaving(false);
    }
  };

  return (
    <>
      <div className="drawer-overlay" onClick={onClose} />
      <div className="drawer">
        {/* Head */}
        <div className="drawer-head">
          <div style={{ width: 36, height: 36, borderRadius: 8, background: store.color || 'var(--brand-navy)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
            {(store.name || '').split(' ').filter(w => w.length > 1).slice(0, 2).map(w => w[0]).join('').toUpperCase()}
          </div>
          <div style={{ flex: 1 }}>
            <h3>Alterar plano</h3>
            <div className="sub">{store.name} · atualmente no <strong style={{ color: 'var(--text-secondary)' }}>{currentPlan?.name || '—'}</strong></div>
          </div>
          <button className="icon-action" onClick={onClose} style={{ width: 32, height: 32 }} disabled={saving}>
            <Icon name="x" size={16} />
          </button>
        </div>

        <div className="drawer-body">
          {plansLoading && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>Carregando planos…</div>
          )}

          {/* Período de cobrança */}
          {!useReal && (
            <div className="drawer-section">
              <h4>Período de cobrança</h4>
              <div className="radio-card-row">
                <div className={'radio-card ' + (billingPeriod === 'monthly' ? 'selected' : '')} onClick={() => setBillingPeriod('monthly')}>
                  <div className="title">Mensal</div>
                  <div className="desc">Cobrança a cada 30 dias</div>
                </div>
                <div className={'radio-card ' + (billingPeriod === 'yearly' ? 'selected' : '')} onClick={() => setBillingPeriod('yearly')}>
                  <div className="title">Anual · 20% off</div>
                  <div className="desc">12 meses pelo valor de ~10</div>
                </div>
              </div>
            </div>
          )}

          {/* Escolher plano */}
          <div className="drawer-section">
            <h4>Escolher plano</h4>
            {selectablePlans.map(p => {
              const isCurrent = p.id === currentPlan?.id;
              const isSel     = p.id === selectedPlanId;
              const price     = (!useReal && billingPeriod === 'yearly') ? p.price * 12 * (1 - yearlyDiscount) : p.price;
              const priceLbl  = (!useReal && billingPeriod === 'yearly') ? '/ano' : '/mês';
              return (
                <div key={p.id}
                  className={'plan-option ' + (isSel ? 'selected' : '') + (isCurrent ? ' current' : '')}
                  onClick={() => setSelectedPlanId(p.id)}>
                  <div className="plan-radio" />
                  <div className="plan-info">
                    <div className="plan-name-row">
                      <span className="plan-name">{p.name}</span>
                      {isCurrent && <span className="tag-current">Atual</span>}
                    </div>
                    <div className="plan-limits">
                      <span><strong>{(p.pageviews || 0).toLocaleString('pt-BR')}</strong> pageviews</span>
                      <span>·</span>
                      <span><strong>{p.users || 0}</strong> usuários</span>
                    </div>
                  </div>
                  <div className="plan-price" style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {fmtBRLDrawer(price)}<div className="per">{priceLbl}</div>
                  </div>
                </div>
              );
            })}

            {/* Enterprise — Em breve / rascunho — não aplicável */}
            <div style={{
              padding: '12px 14px', borderRadius: 10,
              border: '1px dashed var(--border)', opacity: 0.55,
              cursor: 'not-allowed', display: 'flex', alignItems: 'center', gap: 12, marginTop: 4,
            }}>
              <div style={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid var(--border-strong)', flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 14, display: 'flex', gap: 8, alignItems: 'center' }}>
                  Enterprise
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', background: 'var(--bg-subtle)', color: 'var(--text-muted)', borderRadius: 20, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Em breve</span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Indisponível no backend — fora do MVP</div>
              </div>
              <div style={{ textAlign: 'right', color: 'var(--text-muted)', fontSize: 13 }}>Consultar</div>
            </div>

            {overLimits.length > 0 && (
              <div className="over-limit-warning">
                <Icon name="alert" size={14} />
                <div><strong>Atenção:</strong> a loja está acima dos limites do plano escolhido em {overLimits.join(', ')}. O lojista precisará ajustar antes da aplicação.</div>
              </div>
            )}
          </div>

          {/* Quando aplicar */}
          {!useReal && !isSamePlan && (
            <div className="drawer-section">
              <h4>Quando aplicar</h4>
              <div className="radio-card-row" style={{ flexDirection: 'column' }}>
                <div className={'radio-card ' + (applyWhen === 'immediately' ? 'selected' : '')} onClick={() => setApplyWhen('immediately')}>
                  <div className="title">
                    Imediatamente
                    {isUpgrade && <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: 12 }}> · cobrança pró-rata hoje</span>}
                  </div>
                  <div className="desc">
                    {isUpgrade   && `Cobrança pró-rata de ${fmtBRLDrawer(proratedToday)} no cartão cadastrado.`}
                    {isDowngrade && 'Crédito pró-rata aplicado na próxima fatura.'}
                  </div>
                </div>
                <div className={'radio-card ' + (applyWhen === 'next_cycle' ? 'selected' : '')} onClick={() => setApplyWhen('next_cycle')}>
                  <div className="title">No próximo ciclo · {typeof fmtDate !== 'undefined' ? fmtDate(store.nextDue) : '—'}</div>
                  <div className="desc">Sem cobrança adicional agora. Limites atuais continuam até lá.</div>
                </div>
              </div>
            </div>
          )}

          {/* Resumo financeiro */}
          <div className="drawer-section">
            <h4>Resumo</h4>
            <div className="summary-box">
              <div className="row">
                <span style={{ color: 'var(--text-muted)' }}>Plano atual</span>
                <span>{currentPlan?.name} · {fmtBRLDrawer(currentPlan?.price || 0)}/mês</span>
              </div>
              <div className="row">
                <span style={{ color: 'var(--text-muted)' }}>Novo plano</span>
                <span>{selectedPlan?.name} · {fmtBRLDrawer(useReal ? (selectedPlan?.price || 0) : displayPrice)}{useReal ? '/mês' : priceLabel}</span>
              </div>
              {!useReal && !isSamePlan && (
                <div className="row">
                  <span style={{ color: 'var(--text-muted)' }}>Diferença</span>
                  <span className={priceDelta > 0 ? 'delta-up' : 'delta-down'}>
                    {priceDelta > 0 ? '+' : ''}{fmtBRLDrawer(priceDelta)}{billingPeriod === 'yearly' ? '/ano' : '/mês'}
                  </span>
                </div>
              )}
              {!useReal && applyWhen === 'immediately' && isUpgrade && !isSamePlan && (
                <div className="row total"><span>Cobrança hoje (pró-rata)</span><span>{fmtBRLDrawer(proratedToday)}</span></div>
              )}
            </div>
          </div>

          {/* Motivo obrigatório */}
          {!isSamePlan && (
            <div className="drawer-section">
              <h4>Motivo da alteração <span style={{ color: 'var(--danger)' }}>*</span></h4>
              <textarea
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="Descreva o motivo da alteração de plano (mínimo 5 caracteres)..."
                rows={3}
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: 8,
                  border: `1px solid ${reason.trim().length > 0 && reason.trim().length < 5 ? 'var(--danger)' : 'var(--border-strong)'}`,
                  background: 'var(--bg)', color: 'var(--text)', fontSize: 13,
                  fontFamily: 'inherit', resize: 'vertical', lineHeight: 1.5,
                }}
              />
              {reason.trim().length > 0 && reason.trim().length < 5 && (
                <div style={{ fontSize: 11, color: 'var(--danger)', marginTop: 4 }}>Informe pelo menos 5 caracteres</div>
              )}
            </div>
          )}

          {/* Checkbox de confirmação de impacto */}
          {!isSamePlan && (
            <div style={{ padding: '10px 14px', background: isDowngrade ? 'var(--danger-bg)' : 'var(--warning-bg)', borderRadius: 8, marginBottom: 4 }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={e => setConfirmed(e.target.checked)}
                  style={{ marginTop: 2, accentColor: isDowngrade ? 'var(--danger)' : 'var(--warning)', width: 15, height: 15, flexShrink: 0 }}
                />
                <span style={{ fontSize: 12, color: isDowngrade ? 'var(--danger)' : 'var(--warning)', fontWeight: 500, lineHeight: 1.5 }}>
                  Entendo o impacto desta alteração nas permissões do dashboard da loja.
                </span>
              </label>
            </div>
          )}

          {/* Erro de API */}
          {saveError && (
            <div style={{ padding: '10px 14px', background: 'var(--danger-bg)', borderRadius: 8, marginTop: 10, fontSize: 12, color: 'var(--danger)', display: 'flex', gap: 8, alignItems: 'center' }}>
              <Icon name="alert" size={13} />{saveError}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="drawer-foot">
          <span className="note">
            {isSamePlan
              ? 'Nenhuma alteração selecionada'
              : !reason.trim() || reason.trim().length < 5
                ? 'Informe o motivo para continuar'
                : 'Permissões serão sincronizadas no tenant'}
          </span>
          <button className="btn ghost" onClick={onClose} disabled={saving}>Cancelar</button>
          <button
            className={'btn ' + (isDowngrade ? 'danger-outline' : 'primary')}
            disabled={isSamePlan || !canConfirm}
            onClick={handleConfirm}
            style={{ minWidth: 130 }}
          >
            {saving ? (
              <><Icon name="refresh" size={13} style={{ animation: 'spin 0.8s linear infinite' }} />Salvando…</>
            ) : isUpgrade ? (
              <><Icon name="arrowUp" size={13} />Fazer upgrade</>
            ) : isDowngrade ? (
              <><Icon name="arrowDown" size={13} />Aplicar downgrade</>
            ) : (
              'Nenhuma alteração'
            )}
          </button>
        </div>
      </div>
    </>
  );
};

export {
  ChangePlanDrawer,
};
